import { useMutation, useQuery, useQueryClient } from "react-query";
import { IDrone, IPaginatedResponce } from "@drone-parts.garphild.pro/interfaces";
import { fetchDrones, updateDrone } from "../../queries/drones";
import ordersStorage from "../../storages/orders.storage";
import { useAtomValue, useSetAtom } from "jotai";
import {
  availableDroneModelsAtom, availableTemplatesAtom, currentPageAtom,
  inventarizationModeAtom, pageSizeAtom, searchTextAtom,
  selectedDroneModelsAtom, selectedProductTypeAtom, selectedTemplatesAtom,
  selectedStatusesAtom, showSentAtom
} from "../../pages/drones-page/state";
import templatesStorage from "../../storages/templatesStorage";

function useDronesList() {
  const searchText = useAtomValue<string>(searchTextAtom);
  const statuses = useAtomValue<string[]>(selectedStatusesAtom);
  const inventarizationMode = useAtomValue(inventarizationModeAtom);
  const selectedDronesModels = useAtomValue(selectedDroneModelsAtom);
  const selectedTemplates = useAtomValue(selectedTemplatesAtom);
  const setAvailableDroneModels = useSetAtom(availableDroneModelsAtom);
  const setAvailableTemplates = useSetAtom(availableTemplatesAtom);
  const showSent = useAtomValue(showSentAtom);
  const page = useAtomValue(currentPageAtom);
  const pageSize = useAtomValue(pageSizeAtom);
  const selectedProductType = useAtomValue(selectedProductTypeAtom);

  const { refetch, status, data: drones } = useQuery<IPaginatedResponce<IDrone>>(
    [
      'drones',
      {
        statuses,
        dronesModels: selectedDronesModels,
        templates: selectedTemplates,
        page,
        pageSize,
        inventarizationMode,
        showSent,
        searchString: searchText,
        itemTypes: [selectedProductType],
      }],
    fetchDrones,
    {
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 5,
      onSuccess: (data) => {
        setAvailableDroneModels(data.dronesModels || []);
        
        // Load templates for dropdown if they're not already loaded
        if (!templatesStorage.templates.length) {
          templatesStorage.loadTemplates().then(() => {
            const filtered = templatesStorage.templates.filter(t => t.type === selectedProductType);
            const templateOptions = filtered.map(template => ({
              label: template.title,
              value: template._id
            }));
            setAvailableTemplates(templateOptions);
          });
        } else {
          const filtered = templatesStorage.templates.filter(t => t.type === selectedProductType);
          const templateOptions = filtered.map(template => ({
            label: template.title,
            value: template._id
          }));
          setAvailableTemplates(templateOptions);
        }
        
        ordersStorage.loadOrders();
      }
    }
  );

  const droneMutation = useMutation({
    mutationFn: updateDrone,
    onSettled: () => {
      queryClient.invalidateQueries('drones');
    }
  });

  const queryClient = useQueryClient();

  const {
    total: dronesTotal,
    data: dronesList,
    toOrder,
  } = drones || { dronesList: [], plannedByStatuses: {}, toOrder: {}, models: [] };

  return {
    dronesList,
    dronesTotal,
    status,
    droneMutation,
    toOrder,
    refresh: () => refetch(),
  }
}

export default useDronesList;
